// ActivityHeartbeat.tsx · v12.10.10 (#1396 / #1397) · 2026-10-08 · VoiceSpectrum AI
// THE BROWSER AND THE SERVER MUST AGREE ON WHAT "IDLE" MEANS.
// The server ends a session at LastSeenAt + idle minutes, and LastSeenAt moves only when a REQUEST arrives. A person
// reading a long transcript, or scrolling the Messages list, makes no request - so the browser (which counts mouse and
// keys) thought they were active while the server counted them idle, and the next click signed them out.
// Now: real activity (mouse, keys, touch, wheel, and scrolling INSIDE any scroll area - #1397: 'scroll' does not bubble
// from inner containers and 'wheel' was never listened to) marks the tab active; at most once per `everyMs` an active
// tab sends one cheap authenticated GET, which the server's session check counts as "seen". No activity = no ping, so a
// genuinely idle tab still times out exactly as configured.
import { useEffect } from 'react';

export const HEARTBEAT_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click', 'wheel', 'scroll'] as const;

/** Listen for activity in the CAPTURE phase on document, so scrolls inside inner containers are seen too. */
export function addActivityListeners(fn: () => void): () => void {
  const opts: AddEventListenerOptions = { passive: true, capture: true };
  HEARTBEAT_EVENTS.forEach((e) => document.addEventListener(e, fn, opts));
  return () => HEARTBEAT_EVENTS.forEach((e) => document.removeEventListener(e, fn, opts));
}

/** Ping the server while the person is active. `ping` must be a cheap authenticated GET. */
export function useActivityHeartbeat(enabled: boolean, ping: () => Promise<unknown>, everyMs = 3 * 60 * 1000): void {
  useEffect(() => {
    if (!enabled) return;
    let active = false;
    let lastPing = Date.now();
    const off = addActivityListeners(() => { active = true; });
    const t = window.setInterval(() => {
      if (!active || Date.now() - lastPing < everyMs) return;
      active = false; lastPing = Date.now();
      ping().catch(() => { /* a failed ping is not an error the person needs to see; the next request will tell */ });
    }, 15000);
    return () => { off(); window.clearInterval(t); };
  }, [enabled, ping, everyMs]);
}

/** Component form for shells. */
export default function ActivityHeartbeat({ enabled, ping, everyMs }: { enabled: boolean; ping: () => Promise<unknown>; everyMs?: number }) {
  useActivityHeartbeat(enabled, ping, everyMs);
  return null;
}
